import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SiX, SiYoutube, SiInstagram, SiGithub, SiMeta, SiGoogle } from "react-icons/si";
import { Linkedin, MessageCircle, Phone, Mail, MapPin, Shield, ShieldCheck, Lock, CheckCircle2 } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";
import { BRAND } from "@/lib/brand";

const cols = [
  {
    title: "Product",
    links: [
      { label: "WhatsApp Business API", href: "#" },
      { label: "WhatsApp Onboarding", href: "/whatsapp-onboarding", internal: true, highlight: true },
      { label: "Shared Team Inbox", href: "#" },
      { label: "AI Chatbots", href: "#" },
      { label: "Flow Builder", href: "#" },
      { label: "Broadcast", href: "#" },
      { label: "Integrations", href: "#" },
      { label: "Pricing", href: "#pricing" },
      { label: "Compare Platforms", href: "#comparison" },
    ],
  },
  {
    title: "Industries",
    links: [
      "Travel", "Real Estate", "Education", "Healthcare", "Ecommerce", "Automobile", "Finance", "Manufacturing",
    ].map((l) => ({ label: l, href: "#industries" })),
  },
  {
    title: "Company",
    links: [
      "About Wappit", "Customers", "Green Tick Verification", "Blog", "Contact", "Careers",
    ].map((l) => ({ label: l, href: "#" })),
  },
];

export const Footer = ({ activeLegal } = {}) => {
  const legalCls = (slug) =>
    activeLegal === slug
      ? "text-white font-semibold underline underline-offset-4 decoration-emerald-400"
      : "hover:text-white";
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setErrorMsg("");
    try {
      await subscribeNewsletter(email);
      setSuccess(true);
      setEmail("");
      setTimeout(() => setSuccess(false), 6000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong";
      setErrorMsg(typeof msg === "string" ? msg : "Subscription failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-slate-950 text-slate-300">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="[&_span]:text-white">
              <Logo tone="light" />
            </div>
            <p className="mt-5 text-sm text-slate-400 max-w-sm">
              The Official WhatsApp Business API platform for Indian businesses — broadcasts, chatbots, shared inbox, multi-agent collaboration.
            </p>

            <ul className="mt-6 space-y-2 text-sm text-slate-300">
              <li className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-emerald-500" />
                <a href={BRAND.whatsapp.waLink} target="_blank" rel="noreferrer" className="hover:text-white">
                  {BRAND.whatsapp.display} · Chat on WhatsApp
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-emerald-500" />
                <a href={`tel:${BRAND.whatsapp.number}`} className="hover:text-white">{BRAND.whatsapp.display}</a>
                <span className="text-slate-500">· {BRAND.support.hours} IST</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-emerald-500" />
                <a href={`mailto:hello@${BRAND.domain}`} className="hover:text-white">hello@{BRAND.domain}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span data-testid="footer-address">
                  {BRAND.address.line1},<br />
                  {BRAND.address.line2},<br />
                  {BRAND.address.line3}
                </span>
              </li>
            </ul>
            <div className="mt-6 rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-4">
              <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-300">Business Hours</div>
              <div className="text-sm font-semibold text-white mt-0.5">9:00 AM – 7:00 PM IST · Mon to Sat</div>
            </div>
            <p className="mt-4 text-xs text-slate-400 italic" data-testid="footer-trust-statement">
              Trusted by {BRAND.trust.clients} Indian businesses for WhatsApp automation, chatbot solutions and customer engagement.
            </p>
            <form onSubmit={submit} className="mt-6 relative" data-testid="newsletter-form">
              <AnimatePresence mode="wait">
                {success ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.97 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="newsletter-success"
                    className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-400/30 px-4 py-3.5 flex items-center gap-3"
                  >
                    <motion.div
                      initial={{ scale: 0 }} animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 360, damping: 16, delay: 0.05 }}
                      className="h-9 w-9 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/40"
                    >
                      <CheckCircle2 className="h-5 w-5 text-white" />
                    </motion.div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-white">Thank you!</div>
                      <div className="text-xs text-emerald-200/90">You&apos;ve successfully subscribed to Wappit updates.</div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="flex flex-col sm:flex-row gap-2"
                  >
                    <Input
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setErrorMsg(""); }}
                      placeholder="your@email.com"
                      type="email"
                      required
                      data-testid="newsletter-email-input"
                      className="rounded-full bg-slate-900 border-slate-800 text-white placeholder:text-slate-500 focus-visible:ring-emerald-500"
                    />
                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="newsletter-submit-btn"
                      className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-5"
                    >
                      {loading ? "..." : "Subscribe"}
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
              {errorMsg && (
                <div data-testid="newsletter-error" className="mt-2 text-[11px] font-semibold text-rose-300">{errorMsg}</div>
              )}
            </form>
            <div className="mt-6 flex items-center gap-3">
              {[SiX, Linkedin, SiYoutube, SiInstagram, SiGithub].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  className="h-9 w-9 rounded-full bg-slate-900 hover:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                  aria-label="social"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
            {cols.map((c) => (
              <div key={c.title}>
                <div className="text-sm font-bold text-white tracking-wider uppercase">{c.title}</div>
                <ul className="mt-4 space-y-2.5">
                  {c.links.map((l) => (
                    <li key={l.label}>
                      {l.internal ? (
                        <Link
                          to={l.href}
                          data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`text-sm transition-colors ${l.highlight ? "text-emerald-300 font-semibold hover:text-emerald-200" : "text-slate-400 hover:text-white"}`}
                        >
                          {l.label}{l.highlight && " →"}
                        </Link>
                      ) : (
                        <a href={l.href} className="text-sm text-slate-400 hover:text-white transition-colors">{l.label}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Trust hierarchy — centered, stacked. Copyright comes after this block. */}
        <div className="mt-12 pt-10 border-t border-slate-800 space-y-8" data-testid="footer-trust">
          {/* Official Partnerships */}
          <div data-testid="footer-partnerships" className="text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Official Partnerships</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              <a
                href="https://www.facebook.com/business/partner-directory/search?solution_type=messaging&id=1148174632272895"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="partner-meta"
                title="Meta Business Partner"
                aria-label="Meta Business Partner"
                className="inline-flex items-center gap-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 backdrop-blur px-4 py-2.5 transition-all duration-[240ms] hover:-translate-y-0.5"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0866FF] to-[#0053E6] text-white ring-1 ring-white/10">
                  <SiMeta className="h-4 w-4" />
                </span>
                <span className="leading-tight text-left">
                  <span className="block text-[9px] font-bold uppercase tracking-[0.16em] text-slate-400">Official</span>
                  <span className="block text-[13px] font-extrabold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Meta Business Partner</span>
                </span>
              </a>
              <a
                href="https://www.google.com/partners/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="partner-google"
                title="Google Partner"
                aria-label="Google Partner"
                className="inline-flex items-center gap-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 backdrop-blur px-4 py-2.5 transition-all duration-[240ms] hover:-translate-y-0.5"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#4285F4] via-[#EA4335] to-[#FBBC05] text-white ring-1 ring-white/10">
                  <SiGoogle className="h-4 w-4" />
                </span>
                <span className="leading-tight text-left">
                  <span className="block text-[9px] font-bold uppercase tracking-[0.16em] text-slate-400">Verified</span>
                  <span className="block text-[13px] font-extrabold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Google Partner</span>
                </span>
              </a>
            </div>
          </div>

          {/* Platform Security */}
          <div data-testid="footer-security" className="text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Platform Security</p>
            <div className="mt-4 flex flex-wrap lg:flex-nowrap items-center justify-center gap-2" data-testid="footer-badges">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-emerald-400/25 backdrop-blur px-2.5 py-1.5 text-[11px] font-bold text-emerald-300 hover:bg-white/10 transition-colors whitespace-nowrap">
                <Shield className="h-3 w-3" /> Official WhatsApp Business API
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-blue-400/25 backdrop-blur px-2.5 py-1.5 text-[11px] font-bold text-blue-300 hover:bg-white/10 transition-colors whitespace-nowrap">
                <ShieldCheck className="h-3 w-3" /> Official Meta WhatsApp Cloud API
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/15 backdrop-blur px-2.5 py-1.5 text-[11px] font-bold text-slate-300 hover:bg-white/10 transition-colors whitespace-nowrap">
                <Lock className="h-3 w-3" /> End-to-end Encrypted
              </span>
            </div>
          </div>
        </div>

        {/* Copyright — sits AFTER trust hierarchy so the reading order is Nav → Partnerships → Security → Copyright */}
        <div className="mt-8 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <span className="text-center sm:text-left">© {new Date().getFullYear()} Wappit Technologies. {BRAND.url} · All rights reserved.</span>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-5">
            <Link to="/blog" className={legalCls("blog")} data-testid="footer-blog-link">Blog</Link>
            <Link to="/privacy-policy" className={legalCls("privacy-policy")} data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className={legalCls("terms-and-conditions")} data-testid="footer-terms-link">Terms &amp; Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
